import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { useCheckItemBuilder } from 'Src/feature/check-item/useCheckItemBuilder';
import { useCheckItemName } from 'Src/feature/check-item/useCheckItemName';
import { CheckItemDetailButton } from 'Src/feature/check-item-detail/CheckItemDetailButton';
import { CheckItemDetailNote } from 'Src/feature/check-item-detail/CheckItemDetailNote';
import { CheckItemDetailSeatPosition } from 'Src/feature/check-item-detail/CheckItemDetailSeatPosition';
import { FormField } from 'Src/feature/form/FormField';
import { ModifierButton, ModifierButtonProps } from 'Src/feature/modifier/ModifierButton';
import { ModifierCard } from 'Src/feature/modifier/ModifierCard';
import { ModifierCommandPalette } from 'Src/feature/modifier-command/ModifierCommandPalette';
import { PageMain } from 'Src/feature/page/PageMain';
import { useSeatAssignment } from 'Src/feature/seat-assignment/useSeatAssignment';

import { UsedCurrentCheckItemButtons } from './useCurrentCheckItemButtons';

import styles from './CheckItemPageNewEdit.module.scss';

const sectionId = 'CheckItemPageNewEdit';

const smoothSnap = ({ container, content, id }: { container: HTMLDivElement | null, content: HTMLDivElement | null, id: string }) => {
  const element = content?.querySelector(`[data-card-id="${id}"]`) as HTMLElement | null;
  container?.scrollTo({ top: element?.offsetTop || 0, behavior: 'smooth' });
};

interface CheckItemPageNewEditProps {
  checkItemButtons: UsedCurrentCheckItemButtons;
  onGoBack: () => void;
}
/**
 *
 * @description
 * - During a "new check item" state when the new check item has modifiers, there are some operations to be aware of:
 *  - user will not be able to run "some" operations that require data to be saved to the server
 *  - user will be able to run "some" operations that do not require data to be saved to the server
 */
const CheckItemPageNewEdit: FC<CheckItemPageNewEditProps> = ({ checkItemButtons, onGoBack }) => {
  const { t } = useTranslation();

  const checkItemBuilder = useCheckItemBuilder();
  const { getCheckItemName } = useCheckItemName();
  const seatAssignment = useSeatAssignment();

  const cardScrollContainerRef = useRef(null as null | HTMLDivElement);
  const cardScrollContainerContentRef = useRef(null as null | HTMLDivElement);
  const noteRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const container = cardScrollContainerRef.current;
  const content = cardScrollContainerContentRef.current;

  // Get reactive discount data from checkItemButtons instead of builder snapshot
  const currentCheckItem = checkItemButtons.activeCheckItems.find(
    (item) => item.id === checkItemBuilder.source.checkItem?.id
  );
  const discounts = (currentCheckItem?.discounts || []).filter((discount) => discount.status === 'Enabled');

  const handleGoBack = () => {
    onGoBack();
    checkItemBuilder.openPriceSet(undefined);
  };

  return (
    <PageMain
      data-testid={sectionId}
      header={(
        <Flex horizontal gap="sm" padding="sm" alignItems="center" fullHeight fullWidth>
          <Copy
            color="textPrimary"
            variant="h6"
            value={getCheckItemName({
              checkItem: checkItemBuilder.source.checkItem,
              menuItemName: checkItemBuilder.source.menuItem?.name,
            })}
            fullWidth
          />
          <FormField
            type="numberStepper"
            pill
            size="sm"
            value={checkItemBuilder.built.quantity}
            min={1}
            onChange={(event, nextValue) => {
              checkItemBuilder.updateQuantity(nextValue);
            }}
            disabled={!checkItemBuilder.can.edit.quantity}
          />
          <CTA
            label={checkItemBuilder.can.save ? t('labels.cancel') : t('labels.back')}
            onClick={handleGoBack}
            variant="outlined"
            color="textSecondary"
            size="sm"
            minWidth="md"
          />
          <CTA
            color="primary"
            label={t('labels.save')}
            disabled={!checkItemBuilder.can.save}
            onClick={() => {
              void checkItemBuilder.onSave({ onSuccess: handleGoBack, openPrice: checkItemBuilder.openPrice });
            }}
            size="sm"
            minWidth="md"
          />
        </Flex>
      )}
      main={(
        <Flex gap="sm" className={styles.main_root}>
          <Flex horizontal gap="sm" className={styles.main_container}>
            <div className={styles.main_buttons}>
              {seatAssignment.type === 'Required' && (
                <CheckItemDetailButton
                  label={t('labels.seatAssignment')}
                  onClick={() => {
                    // scroll into view...
                    smoothSnap({ container, content, id: 'seat-position' });
                  }}
                />
              )}
              {checkItemBuilder.modifiers.tree?.root?.children.map((rootChild) => {
                const selections: ModifierButtonProps['selections'] = [];
                // walk a whole tree to collect selections
                rootChild.walk('down', (child) => {
                  // ignore "self"
                  if (rootChild.path.full !== child.path.full) {
                    if (child.isSelected) {
                      const label = child.name;
                      selections.push({
                        label,
                        valid: child.valid,
                        // pass in the last selection that was relative to the command palette
                        command: child.selections.last?.command,
                        depth: child.depth,
                        quantity: child.selections.quantity,
                      });
                    }
                  }
                });
                return (
                  <ModifierButton
                    // unique key resolves stale state
                    // - "modifier.path.full" could be the same per check item
                    // - "checkItemId" added for more unique-ness
                    key={`ModifierButton-${checkItemBuilder.source.checkItem?.id || 'new-check-item'}-${rootChild.path.full}`}
                    name={rootChild.name}
                    disabled={false}
                    valid={rootChild.valid}
                    required={Boolean(rootChild.choices?.required)}
                    selected={rootChild.isSelected}
                    selections={selections}
                    onClick={() => {
                      // type script guard (this ref will be available by the time we have a tap event)
                      checkItemBuilder.modifiers.onItemSelect(rootChild);
                      // scroll into view...
                      smoothSnap({ container, content, id: rootChild.path.full });
                    }}
                  />
                );
              })}
              {seatAssignment.type === 'Optional' && (
                <CheckItemDetailButton
                  label={t('labels.seatAssignment')}
                  onClick={() => {
                    // scroll into view...
                    smoothSnap({ container, content, id: 'seat-position' });
                  }}
                />
              )}
              <CheckItemDetailButton
                label={t('labels.note')}
                onClick={() => {
                  // scroll into view...
                  smoothSnap({ container, content, id: 'note' });
                  // focus the note input
                  noteRef.current?.focus();
                }}
              />
            </div>
            <div ref={cardScrollContainerRef} className={styles.main_cardScrollContainer}>
              <div ref={cardScrollContainerContentRef} className={styles.main_cardsContainer}>
                {discounts.length > 0 && (
                  <Flex gap="sm">
                    {discounts.map((discount) => {
                      const itemName = getCheckItemName({
                        checkItem: checkItemBuilder.source.checkItem,
                        menuItemName: checkItemBuilder.source.menuItem?.name,
                      });

                      const discountText = discount.type === 'Percent'
                        ? t('discount.labels.offPercentWithItem', { amount: discount.value, itemName })
                        : t('discount.labels.offAmountWithItem', { amount: discount.value, itemName });

                      const itemTotal = checkItemBuilder.source.checkItem?.gross || 0;
                      const calculatedAmount = discount.type === 'Percent'
                        ? t('labels.util.currency', { value: (itemTotal * (discount.value / 100)).toFixed(2) })
                        : '';

                      return (
                        <Flex
                          key={discount.id}
                          horizontal
                          alignItems="center"
                          gap="sm"
                          className={styles.discount_item}
                          onClick={() => {
                            if (checkItemBuilder.source.checkItem?.id) {
                              checkItemButtons.modals.discount.onOpen(checkItemBuilder.source.checkItem.id);
                            }
                          }}
                        >
                          <FontIcon name="sell" size="sm" className={styles.discount_info_icon} />
                          <Copy variant="body" weight="bold" value={discountText} className={styles.discount_label} />
                          {discount.type === 'Percent' && (
                            <Copy variant="body" value={calculatedAmount} className={styles.discount_calculated} />
                          )}
                          <FontIcon name="edit" size="sm" className={styles.discount_edit_icon} />
                        </Flex>
                      );
                    })}
                  </Flex>
                )}
                {
                  seatAssignment.type === 'Required' && (
                    <CheckItemDetailSeatPosition
                      value={checkItemBuilder.built.seatNames}
                      label={t('labels.required_lowerCase')}
                      onChange={checkItemBuilder.updateSeatNames}
                      data-card-id="seat-position"
                    />
                  )
                }
                {checkItemBuilder.modifiers.tree?.root?.children.map((rootChild) => {
                  const rootChildKey = rootChild.path.full;
                  return (
                    <ModifierCard
                      // unique key resolves stale state
                      // - "modifier.path.full" could be the same per check item
                      // - "checkItemId" added for more unique-ness
                      key={`ModifierCard-${checkItemBuilder.source.checkItem?.id || 'new-check-item'}-${rootChildKey}`}
                      // "data-card-id" used as a selector to scroll card into view when "main" ModifierButton(s) are clicked
                      data-card-id={rootChildKey}
                      modifierPath={rootChildKey}
                    />
                  );
                })}
                {
                  seatAssignment.type === 'Optional' && (
                    <CheckItemDetailSeatPosition
                      value={checkItemBuilder.built.seatNames}
                      label={t('labels.optional_lowerCase')}
                      onChange={checkItemBuilder.updateSeatNames}
                      data-card-id="seat-position"
                    />
                  )
                }
                <CheckItemDetailNote
                  disabled={!checkItemBuilder.can.edit.note}
                  onChange={(next) => checkItemBuilder.updateNote(next)}
                  value={checkItemBuilder.built.note}
                  data-card-id="note"
                  inputRef={(ref) => {
                    noteRef.current = ref;
                  }}
                />
              </div>
            </div>
          </Flex>
          <ModifierCommandPalette />
        </Flex>
      )}
    />
  );
};

export { CheckItemPageNewEdit, sectionId };
